import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { listingsService } from "../services/listingsService";
import { messagesService } from "../services/messagesService";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import ReviewForm from "../components/ReviewForm";
import ReviewList from "../components/ReviewList";
import AffiliateProducts from "../components/AffiliateProducts";
import QualityAssuranceDashboard from "../components/QualityAssuranceDashboard";
import { isQualityAssuranceUiEnabled } from "../config/featureFlags";
import PaymentMethodSelector from "../components/PaymentMethodSelector";
import { purchasesService } from "../services/purchasesService";
import { paymentService } from "../services/paymentService";
import { supabase } from "../lib/supabase";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [messaging, setMessaging] = useState(false);
  const [markingAsSold, setMarkingAsSold] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [sellerListings, setSellerListings] = useState([]);
  const [loadingSellerListings, setLoadingSellerListings] = useState(false);
  const [showQADashboard, setShowQADashboard] = useState(false);
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);
  const [hasTransaction, setHasTransaction] = useState(false);

  useEffect(() => {
    const loadListing = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await listingsService.getListing(id);
        setItem(data);
        
        // Check if there are existing transactions for this listing
        if (data) {
          const transactionsResult = await paymentService.getListingTransactions(data.id);
          if (transactionsResult.success) {
            setHasTransaction(transactionsResult.data.length > 0);
          }
        }
      } catch (err) {
        console.error('Error loading listing:', err);
        setError("Failed to load listing");
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      loadListing();
    }
  }, [id]);

  // Handle payment completion
  const handlePaymentComplete = (transactionData) => {
    console.log('Payment completed:', transactionData);
    setShowPaymentSelector(false);
    setHasTransaction(true);
    // Optionally refresh the listing data
    // You could also show a success message
  };

  // Handle review URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('review') === 'true') {
      setShowReviewForm(true);
      // Scroll to review section after a short delay
      setTimeout(() => {
        const reviewSection = document.getElementById('review-section');
        if (reviewSection) {
          reviewSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, [location.search]);

  // Load seller's other listings
  useEffect(() => {
    const loadSellerListings = async () => {
      if (!item || !item.user_id) return;
      
      setLoadingSellerListings(true);
      try {
        const allListings = await listingsService.getListings();
        // Filter out current listing and only show active listings from the same seller
        const otherListings = allListings.filter(listing => 
          listing.user_id === item.user_id && 
          listing.id !== item.id && 
          listing.status === 'active'
        ).slice(0, 4); // Show max 4 other listings
        
        setSellerListings(otherListings);
      } catch (err) {
        console.error('Error loading seller listings:', err);
      } finally {
        setLoadingSellerListings(false);
      }
    };

    loadSellerListings();
  }, [item]);

  const handleMessageSeller = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!item || user.id === item.user_id) {
      return; // Can't message yourself
    }

    try {
      setMessaging(true);
      setError(''); // Clear any previous errors
      
      console.log('Starting conversation for listing:', item.id, 'with seller:', item.user_id);
      
      // Get or create conversation
      const conversation = await messagesService.getOrCreateConversation(
        item.id,
        user.id,
        item.user_id
      );

      console.log('Conversation created/found:', conversation);

      // Navigate to chat
      navigate(`/messages/${conversation.id}`);
    } catch (err) {
      console.error('Error starting conversation:', err);
      setError(`Failed to start conversation: ${err.message}`);
    } finally {
      setMessaging(false);
    }
  };

  const handleMarkAsSold = async () => {
    if (!user || !item || user.id !== item.user_id) return;
    
    const buyerEmail = prompt("Enter the buyer's email address to mark this item as sold:");
    if (!buyerEmail) return;

    setMarkingAsSold(true);
    setError('');

    try {
      // For now, we'll create a purchase record with a placeholder buyer ID
      // In a real app, you'd look up the buyer by email first
      const { data: buyerData, error: buyerError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', buyerEmail)
        .single();

      if (buyerError || !buyerData) {
        throw new Error('Buyer not found. Please make sure they have an account with this email.');
      }

      await purchasesService.markAsSold(item.id, buyerData.id);
      
      // Update the local state instead of reloading
      setItem(prev => ({ ...prev, status: 'sold' }));
      
      // Show success message
      setError('');
      alert('Item successfully marked as sold!');
    } catch (err) {
      console.error('Error marking as sold:', err);
      if (err.message.includes('already been marked as sold') || err.message.includes('Purchase record already exists')) {
        setError('This item has already been marked as sold.');
      } else {
        setError(err.message || 'Failed to mark item as sold');
      }
    } finally {
      setMarkingAsSold(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-3xl">👶</span>
          </div>
          <p className="text-slate-600">Loading listing...</p>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-3xl">⚠</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Listing not found</h3>
          <p className="text-slate-600 mb-6">{error || "This listing may have been removed."}</p>
          <button 
            className="btn-primary"
            onClick={() => navigate('/browse')}
          >
            Back to Browse
          </button>
        </div>
      </div>
    );
  }

  const currentImage = item.photos?.[selectedImageIndex] || '/placeholder.jpg';

  return (
    <div className="container-center mx-auto max-w-2xl">
      {/* Image Gallery */}
      <div className="mb-6">
        {/* Main Image */}
        <div className="relative flex justify-center">
          <div 
            className="w-96 h-96 max-w-full max-h-[400px] rounded-2xl overflow-hidden bg-slate-100 cursor-zoom-in flex items-center justify-center"
            style={{ width: '24rem', height: '24rem' }}
            onClick={() => setIsZoomed(true)}
          >
            <img 
              className="w-full h-full object-cover bg-white" 
              src={currentImage} 
              alt={item.title}
              style={{ imageRendering: 'auto' }}
            />
          </div>
          {/* Zoom indicator */}
          <div className="absolute top-3 right-3 bg-black/60 text-white px-2 py-1 rounded-full text-xs">
            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
            Click to view
          </div>
        </div>
        
        {/* Thumbnail Gallery */}
        {item.photos && item.photos.length > 1 && (
          <div className="grid grid-cols-4 gap-2 mt-3">
            {item.photos.map((photo, index) => (
              <div 
                key={index} 
                className={`aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-pointer transition-all duration-200 ${
                  index === selectedImageIndex 
                    ? 'ring-2 ring-blue-500 ring-offset-2' 
                    : 'hover:ring-2 hover:ring-blue-300'
                }`}
                onClick={() => setSelectedImageIndex(index)}
              >
                <img 
                  className="w-full h-full object-cover" 
                  src={photo} 
                  alt={`${item.title} ${index + 1}`}
                  style={{ imageRendering: 'high-quality' }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Zoom Modal */}
      {isZoomed && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setIsZoomed(false)}
        >
          <div className="relative max-w-4xl max-h-full flex items-center justify-center">
            <button
              className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors duration-200"
              onClick={() => setIsZoomed(false)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              className="max-w-full max-h-full object-contain bg-white rounded-xl"
              src={currentImage}
              alt={item.title}
              style={{ imageRendering: 'auto' }}
            />
            {/* Navigation arrows for multiple images */}
            {item.photos && item.photos.length > 1 && (
              <>
                {selectedImageIndex > 0 && (
                  <button
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(selectedImageIndex - 1);
                    }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                {selectedImageIndex < item.photos.length - 1 && (
                  <button
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(selectedImageIndex + 1);
                    }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Listing Info */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40">
            <div className="mb-4">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-responsive-xl font-bold text-slate-900">{item.title}</h1>
                {item.status === 'sold' && (
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    SOLD
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`category-badge ${
                  item.category === 'Pre-natal' ? 'category-prenatal' :
                  item.category === 'Clothing' ? 'category-clothing' :
                  item.category === 'Gear' ? 'category-gear' :
                  item.category === 'Toys' ? 'category-toys' :
                  item.category === 'Feeding' ? 'category-feeding' :
                  item.category === 'Nursery' ? 'category-nursery' :
                  'category-other'
                }`}>{item.category}</span>
                <span className="text-sm text-slate-500 capitalize">{item.condition} condition</span>
                {item.age_range && (
                  <div className="text-sm text-slate-500 flex items-center gap-1">
                    <span>👶</span>
                    <span>
                      {item.age_range === '0-3M' && '0-3 months (Newborn)'}
                      {item.age_range === '3-6M' && '3-6 months'}
                      {item.age_range === '6-12M' && '6-12 months'}
                      {item.age_range === '12-24M' && '12-24 months (Toddler)'}
                      {item.age_range === '2-4Y' && '2-4 years (Preschool)'}
                      {item.age_range === '4Y+' && '4+ years (School age)'}
                      {item.age_range === 'All Ages' && 'All Ages'}
                      {!['0-3M', '3-6M', '6-12M', '12-24M', '2-4Y', '4Y+', 'All Ages'].includes(item.age_range) && item.age_range}
                    </span>
                  </div>
                )}
              </div>

              {/* Tags */}
              {item.tags && item.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {item.tags.map((tagId) => {
                    const tagMap = {
                      'moving-out': { label: 'Moving Out', emoji: '📦', color: 'bg-orange-100 text-orange-800 border-orange-200' },
                      'give-away': { label: 'Give Away', emoji: '🎁', color: 'bg-green-100 text-green-800 border-green-200' },
                      'urgent': { label: 'Urgent Sale', emoji: '⚡', color: 'bg-red-100 text-red-800 border-red-200' },
                      'bundle': { label: 'Bundle Deal', emoji: '📦', color: 'bg-purple-100 text-purple-800 border-purple-200' },
                      'new': { label: 'Brand New', emoji: '✨', color: 'bg-blue-100 text-blue-800 border-blue-200' },
                      'negotiable': { label: 'Price Negotiable', emoji: '💰', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
                      'pickup-only': { label: 'Pickup Only', emoji: '🚚', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' }
                    };
                    const tag = tagMap[tagId];
                    if (!tag) return null;
                    return (
                      <span key={tagId} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border ${tag.color}`}>
                        <span>{tag.emoji}</span>
                        <span>{tag.label}</span>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

        <div className="mb-6">
          <div className="price-display text-3xl mb-2">${(item.price_cents / 100).toFixed(2)}</div>
          <div className="text-sm text-slate-500">CAD</div>
        </div>

        {item.description && (
          <div className="mb-6">
            <h3 className="font-semibold text-slate-900 mb-2">Description</h3>
            <p className="text-slate-700 leading-relaxed">{item.description}</p>
          </div>
        )}

        {/* Seller Info */}
        {item.seller_profile && (
          <div className="mb-6 p-4 bg-slate-50 rounded-xl">
            <h3 className="font-semibold text-slate-900 mb-2">Seller</h3>
            <div className="flex items-center gap-3">
              {item.seller_profile.avatar_url ? (
                <img src={item.seller_profile.avatar_url} alt="Seller Avatar" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {item.seller_profile.name ? item.seller_profile.name[0].toUpperCase() : 'U'}
                </div>
              )}
              <div>
                <p className="font-medium text-slate-900">{item.seller_profile.name || 'User'}</p>
                <p className="text-sm text-slate-500">Member since {new Date(item.created_at).getFullYear()}</p>
              </div>
            </div>
          </div>
          )}

        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}


        {/* Action Buttons */}
        <div className="space-y-3">
          {item.status === 'sold' ? (
            <div className="w-full text-center py-3 px-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center justify-center gap-2 text-green-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium">This item has been sold</span>
              </div>
            </div>
          ) : user && user.id !== item.user_id ? (
            <button 
              onClick={handleMessageSeller}
              disabled={messaging}
              className="w-full btn-primary text-lg py-3"
            >
              <span className="flex items-center justify-center gap-2">
                {messaging ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Starting chat...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    {t('listing.messageSeller')}
                  </>
                )}
              </span>
            </button>
          ) : null}

          {/* Payment Button - Show for buyers after they've contacted seller */}
          {user && user.id !== item.user_id && item.status === 'active' && (
            <button 
              onClick={() => setShowPaymentSelector(true)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-200"
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                {hasTransaction ? t('listing.updatePayment') : t('listing.recordPayment')}
              </span>
            </button>
          )}

          {/* Sold Status Display */}
          {item.status === 'sold' && (
            <div className="w-full text-center py-3 px-4 bg-green-100 text-green-800 border border-green-200 rounded-xl font-semibold">
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Item Sold
              </span>
            </div>
          )}

          {/* Quality Assurance Button for Owners */}
          {isQualityAssuranceUiEnabled && user && user.id === item.user_id && (
            <button 
              onClick={() => setShowQADashboard(!showQADashboard)}
              className="w-full btn-secondary mb-4"
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {showQADashboard ? 'Hide Quality Assurance' : 'Quality Assurance'}
              </span>
            </button>
          )}
          
          <button className="w-full btn-secondary">
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Save to Favorites
            </span>
          </button>
        </div>
      </div>

      {/* Affiliate Products Section */}
      <div className="mt-8">
        <AffiliateProducts listing={item} />
      </div>

      {/* Quality Assurance Dashboard Section */}
      {isQualityAssuranceUiEnabled && showQADashboard && user && user.id === item.user_id && (
        <div className="mt-8">
          <QualityAssuranceDashboard 
            listingId={item.id}
            listingCategory={item.category}
            onComplete={() => {
              // Refresh listing data if needed
              console.log('QA completed for listing:', item.id);
            }}
          />
        </div>
      )}

      {/* Seller's Other Listings Section */}
      {sellerListings.length > 0 && (
        <div className="mt-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                More from {item.seller_profile?.name || 'this seller'}
              </h2>
              <button 
                onClick={() => navigate(`/profile/${item.user_id}`)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View all listings →
              </button>
            </div>
            
            {loadingSellerListings ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="aspect-square bg-slate-200 rounded-xl mb-3"></div>
                    <div className="h-4 bg-slate-200 rounded mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {sellerListings.map((listing) => (
                  <div
                    key={listing.id}
                    className="cursor-pointer group"
                    onClick={() => navigate(`/listing/${listing.id}`)}
                  >
                    <div className="relative overflow-hidden rounded-xl mb-3">
                      <img
                        src={listing.photos?.[0] || "/placeholder.jpg"}
                        alt={listing.title}
                        className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`category-badge ${
                          listing.category === 'Pre-natal' ? 'category-prenatal' :
                          listing.category === 'Clothing' ? 'category-clothing' :
                          listing.category === 'Gear' ? 'category-gear' :
                          listing.category === 'Toys' ? 'category-toys' :
                          listing.category === 'Feeding' ? 'category-feeding' :
                          listing.category === 'Nursery' ? 'category-nursery' :
                          'category-other'
                        }`}>
                          {listing.category}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-medium text-slate-900 text-sm mb-1 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {listing.title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-900">
                        ${(listing.price_cents / 100).toFixed(2)}
                      </span>
                      <span className="text-xs text-slate-500 capitalize">
                        {listing.condition}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

        {/* Reviews Section */}
        <div id="review-section" className="mt-8 space-y-6">
          {/* Reviews for this listing */}
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Reviews for this listing</h2>
            <ReviewList userId={item.user_id} listingId={item.id} />
          </div>

          {/* Review Form */}
          {user && user.id !== item.user_id && (
            <>
              {!showReviewForm && (
                <div className="text-center">
                  <button
                    onClick={() => setShowReviewForm(true)}
                    className="btn-primary"
                  >
                    Leave a Review
                  </button>
                </div>
              )}
              
              {showReviewForm && (
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Write a Review</h2>
                  {location.search.includes('review=true') && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-blue-700 text-sm">
                        💡 You're reviewing this item because you purchased it. Share your experience to help other buyers!
                      </p>
                    </div>
                  )}
                  <ReviewForm 
                    listingId={item.id} 
                    sellerId={item.user_id}
                    onReviewSubmitted={() => {
                      setShowReviewForm(false);
                      // Optionally refresh the reviews list
                      window.location.reload();
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Payment Method Selector Modal */}
        {showPaymentSelector && (
          <PaymentMethodSelector
            listing={item}
            buyerId={user?.id}
            onPaymentComplete={handlePaymentComplete}
            onCancel={() => setShowPaymentSelector(false)}
          />
        )}
        
    </div>
  );
}